import { createAsyncThunk } from '@reduxjs/toolkit'
import { apiErrorMessage, isLinkFailure, type ApiConfig } from '@/lib/api'
import { linkOffline, linkOnline } from '@/features/connection/connectionSlice'
import type { AppDispatch, RootState } from './store'

const createTypedThunk = createAsyncThunk.withTypes<{
  state: RootState
  dispatch: AppDispatch
  rejectValue: string
}>()

export function apiConfigOf(state: RootState): ApiConfig {
  return { cloudUrl: state.connection.cloudUrl, adminKey: state.connection.adminKey }
}

/** The slice of the thunk API that call bodies actually need. */
export interface ThunkCtx {
  dispatch: AppDispatch
  getState: () => RootState
}

/**
 * Every admin call shares the same envelope: pull credentials out of the store,
 * report the outcome to the connection indicator, and reject with a message the
 * UI can show verbatim.
 */
export function apiThunk<Returned, Arg = void>(
  type: string,
  body: (arg: Arg, config: ApiConfig, ctx: ThunkCtx) => Promise<Returned>
) {
  return createTypedThunk<Returned, Arg>(type, async (arg, thunkApi) => {
    const config = apiConfigOf(thunkApi.getState())
    const ctx: ThunkCtx = { dispatch: thunkApi.dispatch, getState: thunkApi.getState }
    try {
      const result = await body(arg, config, ctx)
      thunkApi.dispatch(linkOnline())
      return result
    } catch (err) {
      const message = apiErrorMessage(err)
      // The server answering with a 4xx means the link is fine — our request
      // was not. Only transport faults and rejected keys turn the dot red.
      if (isLinkFailure(err)) thunkApi.dispatch(linkOffline(message))
      else thunkApi.dispatch(linkOnline())
      return thunkApi.rejectWithValue(message)
    }
  })
}
